import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminPaymentStatusBadge, AdminRefundStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminPermissionGate } from '../../components/primitives/admin-permission-gate.js';
import { AdminApiError } from '../../api/admin-client.js';
import { formatPaise, formatDateTime } from '../../lib/format.js';
import { useAdminPaymentDetail, useInitiateRefund } from '../hooks/use-payments.js';
import { RefundDialog } from '../components/refund-dialog.js';

export function PaymentDetailPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const { data, isLoading, isError, refetch } = useAdminPaymentDetail(paymentId);
  const initiateRefund = useInitiateRefund();
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const remainingRefundablePaise = data
    ? data.payment.amountPaise -
      data.refunds.filter((r) => r.status !== 'failed').reduce((sum, r) => sum + r.amountPaise, 0)
    : 0;

  const canRefund =
    data && data.payment.method === 'razorpay' && ['captured', 'partially_refunded'].includes(data.payment.status) && remainingRefundablePaise > 0;

  return (
    <AdminContent>
      <AdminPageHeader
        title="Payment detail"
        breadcrumbs={[{ label: 'Payments', to: ROUTE_NAMES.adminPayments }, { label: paymentId ?? '' }]}
      />

      {actionError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      {isLoading && <AdminLoadingState label="Loading payment…" />}
      {isError && <AdminErrorState message="This payment could not be loaded." onRetry={() => void refetch()} />}

      {data && (
        <div className="space-y-4">
          <AdminSectionCard
            title="Payment"
            actions={
              <AdminPermissionGate anyRole={['ADMIN']}>
                {canRefund && (
                  <button
                    type="button"
                    onClick={() => setShowRefundDialog(true)}
                    className="rounded-full border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
                  >
                    Initiate refund
                  </button>
                )}
              </AdminPermissionGate>
            }
          >
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500">Order</dt>
                <dd>
                  <Link to={`${ROUTE_NAMES.adminOrders}/${data.payment.orderNumber}`} className="hover:underline">
                    {data.payment.orderNumber}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Amount</dt>
                <dd className="font-medium text-gray-900">{formatPaise(data.payment.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <AdminPaymentStatusBadge status={data.payment.status} />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Method</dt>
                <dd className="capitalize">{data.payment.method}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Captured</dt>
                <dd>{data.payment.capturedAt ? formatDateTime(data.payment.capturedAt) : '—'}</dd>
              </div>
              {data.payment.failureReason && (
                <div>
                  <dt className="text-gray-500">Failure reason</dt>
                  <dd>{data.payment.failureReason}</dd>
                </div>
              )}
            </dl>
          </AdminSectionCard>

          <AdminSectionCard title="Attempt history">
            {data.attempts.length === 0 ? (
              <p className="text-sm text-gray-500">No attempts recorded.</p>
            ) : (
              <ul className="space-y-1">
                {data.attempts.map((attempt) => (
                  <li key={attempt.id} className="text-sm text-gray-600">
                    #{attempt.attemptNumber} — {attempt.status}
                    {attempt.errorDescription ? ` (${attempt.errorDescription})` : ''} ·{' '}
                    {formatDateTime(attempt.attemptedAt)}
                  </li>
                ))}
              </ul>
            )}
          </AdminSectionCard>

          <AdminSectionCard title="Refunds">
            {data.refunds.length === 0 ? (
              <p className="text-sm text-gray-500">No refunds yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.refunds.map((refund) => (
                  <li key={refund.id} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      {formatPaise(refund.amountPaise)}
                      <AdminRefundStatusBadge status={refund.status} />
                      {refund.reason && <span className="text-xs text-gray-400">{refund.reason}</span>}
                    </span>
                    <span className="text-xs text-gray-400">{formatDateTime(refund.initiatedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </AdminSectionCard>
        </div>
      )}

      {showRefundDialog && data && (
        <RefundDialog
          orderNumber={data.payment.orderNumber}
          remainingRefundablePaise={remainingRefundablePaise}
          isSubmitting={initiateRefund.isPending}
          onClose={() => setShowRefundDialog(false)}
          onSubmit={(input) => {
            setActionError(null);
            initiateRefund.mutate(
              { paymentId: data.payment.id, input },
              {
                onSuccess: () => setShowRefundDialog(false),
                onError: (error) =>
                  setActionError(error instanceof AdminApiError ? error.message : 'Could not initiate refund.'),
              },
            );
          }}
        />
      )}
    </AdminContent>
  );
}
